import { createSelector } from '@reduxjs/toolkit'
import { merklebaseAPI } from 'store/api/merklebaseAPI'
import { mapPortfolios } from 'utils/mappers/portfolios'

// Base selectors - select RTK Query cache results
// getPortfolios has two cache entries: { ignoreAUM: true } and { ignoreAUM: false }
const selectPortfoliosResultIgnoreAUM = merklebaseAPI.endpoints.getPortfolios.select({
  ignoreAUM: true,
})
const selectPortfoliosResultWithAUM = merklebaseAPI.endpoints.getPortfolios.select({
  ignoreAUM: false,
})

// Select raw data from results
export const selectPortfoliosRaw = createSelector(
  [selectPortfoliosResultIgnoreAUM],
  (result) => result?.data?.portfolios ?? []
)

export const selectTotalAUM = createSelector(
  [selectPortfoliosResultWithAUM],
  (result) => result?.data?.totalAUM ?? 0
)

// Loading states
export const selectPortfoliosLoading = createSelector(
  [selectPortfoliosResultIgnoreAUM],
  (result) => result?.isLoading ?? result?.isFetching ?? false
)

export const selectTotalAUMLoading = createSelector(
  [selectPortfoliosResultWithAUM],
  (result) => result?.isLoading ?? result?.isFetching ?? false
)

export const selectTotalAUMUninitialized = createSelector(
  [selectPortfoliosResultWithAUM],
  (result) => result?.isUninitialized ?? false
)

export const selectTotalAUMFetching = createSelector(
  [selectPortfoliosResultWithAUM],
  (result) => result?.isFetching ?? false
)

// Mapped portfolios - requires selectedPortfoliosIds from auth (passed as arg)
// Factory selector for use with useSelector in components that have access to selectedPortfoliosIds
export const selectMappedPortfolios = (selectedPortfoliosIds) =>
  createSelector(
    [selectPortfoliosRaw, () => selectedPortfoliosIds],
    (portfolios, ids) => {
      if (!portfolios?.length) return []
      return portfolios
        .map((portfolio) => mapPortfolios({ portfolio, selectedPortfoliosIds: ids, portfolios }))
        .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1))
    }
  )

export const selectPortfoliosById = (selectedPortfoliosIds) =>
  createSelector(
    [selectMappedPortfolios(selectedPortfoliosIds)],
    (mappedPortfolios) =>
      mappedPortfolios.reduce((acc, portfolio) => {
        acc[portfolio.id] = portfolio
        return acc
      }, {})
  )

export const selectSelectedPortfolios = (selectedPortfoliosIds) =>
  createSelector(
    [selectMappedPortfolios(selectedPortfoliosIds)],
    (mappedPortfolios) => mappedPortfolios.filter((portfolio) => portfolio.isSelected)
  )
